import { Grid } from '@mui/material';
import { ODSAnalytics, ODSCopyPaste, ODSNavigationExternalLink } from '@telekom-ods/react-icons';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData } from 'react-router';

import Caas from '../../../../../api/caas';
import Provision from '../../../../../api/provision';
import Button from '../../../../../components/Buttons/Button';
import CodeBlock from '../../../../../components/CodeBlock/CodeBlock';
import Modal from '../../../../../components/Modal';
import OverviewTable from '../../../../../components/OverviewTable/OverviewTable';
import useApiQuery from '../../../../../hooks/useApiQuery';
import { useCopyToClipboard } from '../../../../../hooks/useCopyToClipboard';
import rbacCheck from '../../../../../rbac/rbacCheck';

import CaasClusterNodes from './CaasClusterNodes';
import UpgradeClusterForm from './UpgradeClusterForm';

import { useUserData } from '@/redux/selectors';
import type { ErrorObject } from '@/types/formTypes';
import type { CaasCluster } from '@/types/productTypes';

interface ModalDataType {
  cluster: CaasCluster | null;
  logs: string | null;
}

interface KubeConfigModalDataType {
  cluster: CaasCluster | null;
  config: string | null;
}

interface ClusterConfigResponse {
  cluster: string;
  kubeconfig: string;
}

interface CaasClusterDetailsProps {
  clusterData?: CaasCluster;
}

const KUBECONFIG_DISPLAY_LINE_LENGTH = 120;

const decodeKubeconfig = (encodedConfig: string): string => {
  const binaryString = window.atob(encodedConfig);
  const bytes = Uint8Array.from(binaryString, character => character.charCodeAt(0));

  return new TextDecoder().decode(bytes);
};

const formatKubeconfigForDisplay = (config: string): string => {
  return config
    .split('\n')
    .map(
      line =>
        line
          .match(new RegExp(`.{1,${KUBECONFIG_DISPLAY_LINE_LENGTH.toString()}}`, 'g'))
          ?.join('\n') ?? ''
    )
    .join('\n');
};

const CaasClusterDetails = ({ clusterData }: CaasClusterDetailsProps): React.JSX.Element | null => {
  const intl = useIntl();
  const copyToClipboard = useCopyToClipboard();
  const userData = useUserData();
  const clusterLoaderResult: CaasCluster | null = useLoaderData<CaasCluster | null>();

  const hasValidFields =
    clusterData &&
    Object.values(clusterData).some(
      value => value !== '' && value !== 0 && value !== null && value !== undefined
    );
  const cluster = hasValidFields ? clusterData : clusterLoaderResult;
  const [modalData, setModalData] = useState<ModalDataType | null>(null);
  const [kubeConfigModalData, setKubeConfigModalData] = useState<KubeConfigModalDataType | null>(
    null
  );
  const [upgradeModalOpened, setUpgradeModalOpened] = useState<boolean>(false);
  const [clusterCheckError, setClusterCheckError] = useState<ErrorObject | null>(null);
  const [kubeConfigError, setKubeConfigError] = useState<ErrorObject | null>(null);

  const canUpgradeCluster = rbacCheck(userData.roles, '/reports/caas-clusters:PATCH');

  const { makeApiCall: getCaasClusterCheck } = useApiQuery<string>(
    Caas.fetchClusterCheck as unknown as (args: unknown) => Promise<string>
  );
  const { makeApiCall: getClusterConfig } = useApiQuery<ClusterConfigResponse>(
    Provision.fetchClusterConfigV1 as unknown as (args: unknown) => Promise<ClusterConfigResponse>
  );

  const handleClusterCheck = async (): Promise<void> => {
    try {
      const data = await getCaasClusterCheck(cluster?.name);
      if (!data) {
        setClusterCheckError({ message: 'clusterCheckFetchError' });
        setModalData({ cluster, logs: null });
      } else {
        setModalData({ cluster, logs: data });
        setClusterCheckError(null);
      }
    } catch (error) {
      console.error('Error fetching cluster check data:', error);
      setClusterCheckError({ message: 'clusterCheckFetchError' });
      setModalData({ cluster, logs: null });
    }
  };

  const handleUpgradeRequest = (): void => {
    if (cluster?.type === 'openstack') {
      setUpgradeModalOpened(true);
    } else {
      window.open('https://jira.telekom.de/servicedesk/customer/portal/1293/create/6777', '_blank');
    }
  };

  const handleClusterConfig = async (): Promise<void> => {
    try {
      const data = await getClusterConfig(cluster?.name);
      if (!data) {
        setKubeConfigError({ message: 'kubeconfigFetchError' });
        setKubeConfigModalData({ cluster, config: null });
      } else {
        setKubeConfigModalData({ cluster, config: decodeKubeconfig(data.kubeconfig) });
        setKubeConfigError(null);
      }
    } catch (error) {
      console.error('Error fetching cluster kubeconfig:', error);
      setKubeConfigError({ message: 'kubeconfigFetchError' });
      setKubeConfigModalData({ cluster, config: null });
    }
  };

  const rows = [
    {
      title: { id: 'name' },
      value: cluster?.name,
    },
    {
      title: { id: 'clusterType' },
      value: cluster?.type,
    },
    {
      title: { id: 'juiceGroup' },
      value: cluster?.juice_group,
    },
    {
      title: { id: 'clusterVersion' },
      value: cluster?.version,
    },
    {
      title: { id: 'cloud' },
      value: cluster?.cloud_id,
    },
    {
      title: { id: 'deviceCount' },
      value: cluster?.device_count,
    },
    {
      title: { id: 'owner' },
      value: cluster?.owner,
    },
    {
      title: { id: 'created' },
      value: cluster?.created,
    },
    {
      title: { id: 'lastUpdated' },
      value: cluster?.last_updated,
    },
    {
      title: { id: 'kubeConfig' },
      component: (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='outline'
            disabled={!cluster?.name}
            onClick={() => {
              void handleClusterConfig();
            }}
            icon={<ODSCopyPaste />}
            rightIcon
            label={intl.formatMessage({ id: 'copyKubeConfig' })}
          />
        </div>
      ),
    },
  ];

  return cluster ? (
    <Grid
      container
      direction='column'
      rowGap={8}
    >
      {modalData && (
        <Modal
          maxWidth='md'
          title={intl.formatMessage(
            { id: 'clusterStatusCheckModalTitle' },
            { name: modalData.cluster?.name ?? '' }
          )}
          onSubmit={() => {
            if (modalData.logs) {
              copyToClipboard(modalData.logs);
            }
          }}
          onClose={() => {
            setModalData(null);
          }}
          submitLabel={intl.formatMessage({ id: 'copy' })}
          error={clusterCheckError}
        >
          {modalData.logs && (
            <CodeBlock
              code={modalData.logs}
              language='log'
            />
          )}
        </Modal>
      )}
      {kubeConfigModalData && (
        <Modal
          maxWidth='md'
          title={intl.formatMessage(
            { id: 'kubeConfigModalTitle' },
            { name: kubeConfigModalData.cluster?.name ?? '' }
          )}
          onSubmit={() => {
            if (kubeConfigModalData.config) {
              copyToClipboard(kubeConfigModalData.config);
            }
          }}
          onClose={() => {
            setKubeConfigModalData(null);
          }}
          submitLabel={intl.formatMessage({ id: 'copy' })}
          error={kubeConfigError}
        >
          {kubeConfigModalData.config && (
            <CodeBlock
              code={formatKubeconfigForDisplay(kubeConfigModalData.config)}
              language='yaml'
              wrapLongLines
            />
          )}
        </Modal>
      )}
      {upgradeModalOpened && (
        <UpgradeClusterForm
          clusterName={cluster.name}
          clusterType={cluster.type}
          cloud={cluster.cloud_id}
          owner={cluster.owner}
          onCancel={() => {
            setUpgradeModalOpened(false);
          }}
        />
      )}
      <Grid item>
        <OverviewTable
          header={cluster.name}
          extraActions={[
            {
              variant: 'secondary',
              leftIcon: true,
              icon: <ODSAnalytics />,
              onClick: () => {
                handleClusterCheck();
              },
              label: intl.formatMessage({ id: 'clusterStatusCheck' }),
            },
            {
              variant: 'secondary',
              rightIcon: true,
              icon: <ODSNavigationExternalLink />,
              onClick: handleUpgradeRequest,
              label: intl.formatMessage({
                id: cluster.type !== 'baremetal' ? 'upgrades' : 'requestClusterUpgrade',
              }),
              disabled: !canUpgradeCluster,
            },
          ]}
          rows={rows}
        />
      </Grid>
      <Grid item>
        <CaasClusterNodes cluster={cluster} />
      </Grid>
    </Grid>
  ) : null;
};

export default CaasClusterDetails;
